import React, { memo, useMemo, useRef, useState } from 'react'
import { isPlaced, isSection, positionable } from './fieldTypes'
import TypePicker, { TypeIcon } from './TypePicker'
import { conditionSummary } from './ConditionEditor'
import { hasCondition } from '../conditions'
import { interpolate, useT } from './translations'

export const FIELD_DRAG_TYPE = 'application/x-form-builder-field'

const byOrder = (a, b) => (a.order || 0) - (b.order || 0) || a.id - b.id

export const buildTree = (fields) => {
  const roots = fields.filter((field) => !field.section_id).sort(byOrder)
  const children = {}
  fields.filter((field) => field.section_id).forEach((field) => {
    (children[field.section_id] = children[field.section_id] || []).push(field)
  })
  Object.values(children).forEach((list) => list.sort(byOrder))
  return { roots, children }
}

const FieldRow = memo(({ field, conditionTitle, depth, selected, dropWhere, hasPdf, readOnly, handlers }) => {
  const t = useT()
  const placed = isPlaced(field)

  return (
    <div
      className={`fb-row${selected ? ' selected' : ''}${depth ? ' is-child' : ''}${dropWhere ? ` drop-${dropWhere}` : ''}`}
      draggable={!readOnly || (hasPdf && positionable(field))}
      onDragStart={(event) => handlers.start(event, field)}
      onDragOver={(event) => handlers.over(event, field)}
      onDrop={(event) => handlers.drop(event)}
      onDragEnd={handlers.end}
      onClick={(event) => handlers.select(field.id, event)}
    >
      {!readOnly && <span className="fb-grip"><i className="fas fa-grip-vertical" /></span>}
      <TypeIcon type={field.data_type} small />
      <span className={`fb-row-name${field.name ? '' : ' is-empty'}`}>{field.name || t.untitled}</span>
      {field.is_mandatory && <span className="fb-required" title={t.required}>*</span>}
      {conditionTitle && <i className="fas fa-code-branch fb-conditional" title={conditionTitle} />}
      {hasPdf && positionable(field) &&
        <span className={`fb-placed${placed ? ' is-placed' : ''}`} title={placed ? t.placed : t.not_placed}>
          <i className={placed ? 'fas fa-map-pin' : 'far fa-circle'} />
        </span>
      }
      {!readOnly &&
        <span className="fb-row-actions">
          <button type="button" title={t.duplicate} onClick={(event) => { event.stopPropagation(); handlers.duplicate(field.id) }}>
            <i className="far fa-copy" />
          </button>
          <button type="button" title={t.delete} onClick={(event) => { event.stopPropagation(); handlers.remove(field.id) }}>
            <i className="far fa-trash-alt" />
          </button>
        </span>
      }
    </div>
  )
})

const StructurePanel = ({ fields, selection, hasPdf, readOnly, actions }) => {
  const t = useT()
  const [search, setSearch]       = useState('')
  const [onlyUnplaced, setOnlyUnplaced] = useState(false)
  const [collapsed, setCollapsed] = useState({})
  const [picker, setPicker]       = useState(null)
  const [dragging, setDragging]   = useState(null)
  const [over, setOver]           = useState(null)

  const tree = useMemo(() => buildTree(fields), [fields])
  const dataFields = fields.filter((field) => !isSection(field))
  const unplaced   = dataFields.filter((field) => positionable(field) && !isPlaced(field))

  const select = (id, event) => {
    if (event && (event.shiftKey || event.metaKey || event.ctrlKey)) {
      actions.setSelection((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]))
    } else {
      actions.setSelection([id])
    }
  }

  const targetSection = () => {
    const selected = fields.find((field) => field.id === selection[0])
    if (!selected) { return null }
    return isSection(selected) ? selected.id : selected.section_id
  }

  const drag = {
    over,
    start: (event, field) => {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData(FIELD_DRAG_TYPE, String(field.id))
      event.dataTransfer.setData('text/plain', field.name || '')
      setDragging(field)
    },
    over_: (event, target) => {
      if (!dragging || dragging.id === target.id || readOnly) { return }
      event.preventDefault()
      const rect  = event.currentTarget.getBoundingClientRect()
      const ratio = (event.clientY - rect.top) / rect.height
      let where = ratio < 0.5 ? 'before' : 'after'
      if (isSection(target) && !isSection(dragging) && ratio >= 0.35) { where = 'inside' }
      if (over?.id !== target.id || over?.where !== where) { setOver({ id: target.id, where }) }
    },
    drop: (event) => {
      event.preventDefault()
      if (dragging && over) { move(dragging, over) }
      drag.end()
    },
    end: () => { setDragging(null); setOver(null) }
  }

  const move = (field, { id: targetId, where }) => {
    const target = fields.find((item) => item.id === targetId)
    if (!target) { return }

    let sectionId
    let anchor = target
    if (where === 'inside') {
      sectionId = target.id
    } else {
      sectionId = target.section_id || null
      // A section never goes inside another one: it lands next to the parent
      if (isSection(field) && sectionId) {
        anchor = fields.find((item) => item.id === sectionId)
        sectionId = null
      }
    }

    const section = sectionId && fields.find((item) => item.id === sectionId)
    if (field.data_type === 'table' && section && section.duplicable) {
      window.alert(t.table_not_in_loop)
      return
    }

    const siblings = (sectionId ? tree.children[sectionId] || [] : tree.roots).filter((item) => item.id !== field.id)
    let index = where === 'inside' ? 0 : siblings.findIndex((item) => item.id === anchor.id) + (where === 'after' ? 1 : 0)
    if (index < 0) { index = siblings.length }
    siblings.splice(index, 0, field)

    const items = siblings.map((item, position) => ({ id: item.id, section_id: sectionId, order: position + 1 }))
    if ((field.section_id || null) !== sectionId) {
      const previous = (field.section_id ? tree.children[field.section_id] || [] : tree.roots).filter((item) => item.id !== field.id)
      previous.forEach((item, position) => items.push({ id: item.id, section_id: field.section_id || null, order: position + 1 }))
    }
    actions.reorder(items)
  }

  const handlersRef = useRef({})
  handlersRef.current = {
    start: drag.start, over: drag.over_, drop: drag.drop, end: drag.end,
    select, duplicate: actions.duplicateField, remove: actions.deleteField
  }
  const handlers = useMemo(() => ({
    start:     (...args) => handlersRef.current.start(...args),
    over:      (...args) => handlersRef.current.over(...args),
    drop:      (...args) => handlersRef.current.drop(...args),
    end:       (...args) => handlersRef.current.end(...args),
    select:    (...args) => handlersRef.current.select(...args),
    duplicate: (...args) => handlersRef.current.duplicate(...args),
    remove:    (...args) => handlersRef.current.remove(...args)
  }), [])

  const selected = useMemo(() => new Set(selection), [selection])

  const rowProps = (field, depth) => ({
    field,
    conditionTitle: hasCondition(field.visible_when) ? (conditionSummary(field.visible_when, fields, t) || t.conditional) : null,
    depth,
    selected: selected.has(field.id),
    dropWhere: over && over.id === field.id ? over.where : null,
    hasPdf,
    readOnly,
    handlers
  })

  const matches = (field) => {
    if (onlyUnplaced && !(positionable(field) && !isPlaced(field))) { return false }
    return !search || (field.name || '').toLowerCase().includes(search.toLowerCase())
  }

  const filtering = search || onlyUnplaced

  const renderSection = (section) => {
    const children = tree.children[section.id] || []
    const open = !collapsed[section.id]
    const dropClass = over && over.id === section.id ? ` drop-${over.where}` : ''

    return (
      <div className="fb-section" key={section.id}>
        <div
          className={`fb-section-head${selection.includes(section.id) ? ' selected' : ''}${dropClass}`}
          draggable={!readOnly}
          onDragStart={(event) => drag.start(event, section)}
          onDragOver={(event) => drag.over_(event, section)}
          onDrop={drag.drop}
          onDragEnd={drag.end}
          onClick={(event) => select(section.id, event)}
        >
          <button
            type="button"
            className="fb-caret"
            onClick={(event) => { event.stopPropagation(); setCollapsed({ ...collapsed, [section.id]: open }) }}
          >
            <i className={`fas fa-chevron-${open ? 'down' : 'right'}`} />
          </button>
          <span className="fb-section-name">{section.name || t.untitled}</span>
          {section.duplicable && <i className="fas fa-redo fb-loop" title={t.duplicable} />}
          {hasCondition(section.visible_when) && <i className="fas fa-code-branch fb-conditional" title={conditionSummary(section.visible_when, fields, t) || t.conditional} />}
          <span className="fb-count">{children.length}</span>
          {!readOnly &&
            <span className="fb-row-actions">
              <button type="button" title={t.duplicate} onClick={(event) => { event.stopPropagation(); actions.duplicateField(section.id) }}>
                <i className="far fa-copy" />
              </button>
              <button type="button" title={t.delete} onClick={(event) => { event.stopPropagation(); actions.deleteField(section.id) }}>
                <i className="far fa-trash-alt" />
              </button>
            </span>
          }
        </div>
        {open &&
          <div className="fb-section-body">
            {children.map((child) => <FieldRow key={child.id} {...rowProps(child, 1)} />)}
            {children.length === 0 &&
              <div
                className={`fb-section-empty${over && over.id === section.id && over.where === 'inside' ? ' drop-inside' : ''}`}
                onDragOver={(event) => { if (dragging && !isSection(dragging)) { event.preventDefault(); setOver({ id: section.id, where: 'inside' }) } }}
                onDrop={drag.drop}
              >
                {t.empty_section}
              </div>
            }
          </div>
        }
      </div>
    )
  }

  return (
    <aside className="fb-panel fb-structure">
      <div className="fb-panel-head">
        <div className="fb-panel-title">{t.structure}</div>
        <div className="fb-search">
          <i className="fas fa-search" />
          <input type="search" value={search} placeholder={t.search} onChange={(event) => setSearch(event.target.value)} />
        </div>
        {hasPdf &&
          <button
            type="button"
            className={`fb-chip${onlyUnplaced ? ' active' : ''}${unplaced.length === 0 ? ' is-done' : ''}`}
            onClick={() => setOnlyUnplaced(!onlyUnplaced)}
          >
            {unplaced.length === 0
              ? <><i className="fas fa-check" /> {t.all_placed}</>
              : <><i className="far fa-circle" /> {interpolate(t.unplaced_count, { count: unplaced.length })}</>}
          </button>
        }
      </div>

      <div className="fb-tree" onClick={(event) => { if (event.target === event.currentTarget) { actions.setSelection([]) } }}>
        {filtering
          ? dataFields.filter(matches).map((field) => <FieldRow key={field.id} {...rowProps(field, 0)} />)
          : tree.roots.map((field) => (isSection(field) ? renderSection(field) : <FieldRow key={field.id} {...rowProps(field, 0)} />))
        }
        {!filtering && fields.length === 0 && <div className="fb-empty">{t.empty_form}</div>}
        {filtering && dataFields.filter(matches).length === 0 && <div className="fb-empty">{t.no_match}</div>}
        {hasPdf && !readOnly && unplaced.length > 0 && !filtering &&
          <div className="fb-hint"><i className="fas fa-hand-pointer" /> {t.drag_to_place}</div>
        }
      </div>

      {!readOnly &&
        <div className="fb-panel-foot">
          <div className="fb-add">
            <button type="button" className="fb-button is-primary" onClick={() => setPicker('field')}>
              <i className="fas fa-plus" /> {t.add_field}
            </button>
            <button type="button" className="fb-button" onClick={actions.createSection}>
              <i className="fas fa-layer-group" /> {t.add_section}
            </button>
            {picker &&
              <TypePicker
                onClose={() => setPicker(null)}
                onPick={(type) => {
                  setPicker(null)
                  const sectionId = targetSection()
                  const section = fields.find((field) => field.id === sectionId)
                  actions.createField({ dataType: type, sectionId: type === 'table' && section?.duplicable ? null : sectionId })
                }}
              />
            }
          </div>
        </div>
      }
    </aside>
  )
}

export default StructurePanel
